import type { ArtifactContent } from "../ChatEventTypes.ts";
import type { QueryContext } from "../components/FocusBanner.tsx";
import type { MentionData } from "../components/Mention.tsx";
import type { MentionedVideoItem } from "../components/VideoMention.tsx";
import { readLiveThinkingDetailsOpen } from "./liveThinkingPreference.ts";

export type SpeakingMentionEntry =
	| { kind: "mention"; data: MentionData }
	| { kind: "video"; data: MentionedVideoItem }
	| { kind: "artifact"; data: ArtifactContent };

export type SpeakingBlockState = {
	id: string;
	markdown: string;
	mentions: Record<string, SpeakingMentionEntry>;
	answerType: string;
	complete: boolean;
};

export type ExecutionProgressState = {
	type: "continuous";
	percentage: number;
};

export type ExecutionSegmentState = {
	description?: string;
	progress?: ExecutionProgressState;
};

/**
 * The execution reporting continuous progress (currently only deck generation),
 * shown beneath the thinking tickers. Survives at 100% until the answer arrives,
 * because reaching 100% doesn't mean the step has finished its work.
 */
export type LiveProgressState = {
	executionId: string;
	description?: string;
	percentage: number;
};

export type ExecutionSocialAccountState = {
	handle: string;
	profile_picture_url: string;
	platform: string;
	follower_count: number;
};

export type ExecutionBlockState = {
	id: string;
	segments: ExecutionSegmentState[];
	images: string[];
	socialAccounts: ExecutionSocialAccountState[];
};

export type PlanItemState = {
	id: string;
	description: string;
	status: "pending" | "executing" | "complete";
};

export type PlanSectionState = {
	planId: string;
	items: PlanItemState[];
	expired: boolean;
};

export type ResponseBlock = { kind: "speaking"; block: SpeakingBlockState };

export type NotificationState = {
	id: string;
	title?: string;
	message: string;
};

export type ThinkingImageTickerItem = {
	id: string;
	url: string;
	shape: "circle" | "rounded";
	video_id?: string;
	social_account_id?: string;
};

export type ThinkingLogImage = {
	url: string;
	video_id?: string;
};

export type ThinkingLogEntry =
	| {
			id: string;
			kind: "plan";
			title: string;
			description: string;
	  }
	| {
			id: string;
			kind: "execution";
			title: string;
			description: string;
			images: ThinkingLogImage[];
			socialAccounts: ExecutionSocialAccountState[];
	  };

/** Langchain asking whether it may answer at a deeper effort than was asked for. */
export type EffortUpgradeRequestState = {
	requestId: string;
	suggestedEffort: string;
	defaultEffort: string;
	/** On this browser's clock, which needn't agree with the server's. */
	respondByMs: number;
	/** False when replayed from history, where it was answered long ago. */
	live: boolean;
};

export type EffortUpgradeResolutionState = {
	requestId: string;
	outcome: string;
	resolvedEffort: string;
	/** False when replayed from history. */
	live: boolean;
};

export type AnswerState = {
	reasoningEventsOpen: boolean;
	reasoningToggleOpen: boolean;
	answerAvailable: boolean;
	startedAtMs: number;
	thinkingEndedAtMs?: number;
	tickerTitles: string[];
	tickerImages: ThinkingImageTickerItem[];
	liveProgress?: LiveProgressState;
	thinkingLog: ThinkingLogEntry[];
	plans: PlanSectionState[];
	executions: ExecutionBlockState[];
	responseBlocks: ResponseBlock[];
	effortUpgradeRequest?: EffortUpgradeRequestState;
	effortUpgradeResolution?: EffortUpgradeResolutionState;
};

export type InteractionState = {
	id?: string;
	index: number;
	queryContextId?: string;
	queryContext?: QueryContext;
	question?: string;
	/** Id from this interaction's `artifact_created` event with type `interaction`. */
	artifactId?: string;
	/** Registry public_id when the interaction transcript was autosaved. */
	savedDocumentPublicId?: string;
	/** Favourite flag from hydrated `artifact_created` for the interaction transcript. */
	savedDocumentIsFavourite?: boolean;
	exception?: {
		title?: string;
		message: string;
		identifier?: string;
	};
	ephemeralNotifications: NotificationState[];
	answer?: AnswerState;
};

export type ChatPanelUiState = {
	interactions: InteractionState[];
	latestInteractionId?: string;
	renderingHistory: boolean;
	replyDisabled: boolean;
	isReasoning: boolean;
	nextInteractionIndex: number;
	mentionCounter: number;
	videoMentionCounter: number;
	queryContextCounter: number;
	scrollToInteractionIndex?: number;
};

export function createInitialUiState(): ChatPanelUiState {
	return {
		interactions: [],
		renderingHistory: true,
		replyDisabled: true,
		isReasoning: false,
		nextInteractionIndex: 0,
		mentionCounter: 0,
		videoMentionCounter: 1,
		queryContextCounter: 0,
	};
}

export function createInitialAnswerState(
	renderingHistory: boolean,
	startedAtMs: number = Date.now(),
): AnswerState {
	return {
		reasoningEventsOpen: renderingHistory
			? false
			: readLiveThinkingDetailsOpen(),
		reasoningToggleOpen: renderingHistory,
		answerAvailable: false,
		startedAtMs,
		tickerTitles: [],
		tickerImages: [],
		thinkingLog: [],
		plans: [],
		executions: [],
		responseBlocks: [],
	};
}
